import * as THREE from 'three';
import type { Terrain } from '@/terrain/Terrain';
import { DEFAULT_TAG_RADIUS_METERS, tagCategoryInfo, type SemanticTag, type TagTarget } from '@/semantics/SemanticTag';
import type { TagStore } from '@/semantics/TagStore';

/**
 * The tag tool's canvas half: turn a click into a {@link TagTarget}, and draw what is already tagged.
 *
 * ## Why the markers matter more than the picking
 *
 * Picking a target is three raycasts; the interesting half is the other one. A tag is invisible by
 * construction — it changes no geometry, no colour, nothing the renderer would otherwise draw — so
 * without markers a tagged site and an untagged one look identical, and the only way to find out what
 * the render will be told is to click every object in turn. That is not a feature anybody uses twice.
 *
 * So while the tool is live every tag draws itself on the site it is about: a ring on the ground for
 * a place, a pin over the thing for an object, each in its category's colour and carrying its label.
 * The markers live on `overlayRoot`, which photo mode and all three capture passes hide, so they can
 * never end up in a render — they describe the picture, they are not in it.
 *
 * ## What this does not do
 *
 * It does not create, edit, or store a tag. It converts a pointer event into a target and reports it;
 * `TagBubble` writes the tag and `TagStore` holds it. Everything except this file is testable without
 * a GPU, which is the same split `WandTool` uses and for the same reason.
 */

export interface TagToolDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  terrain: () => Terrain | null;
  tags: TagStore;
  floraRoot: THREE.Object3D;
  siteRoot: THREE.Object3D;
  /**
   * Where a tag's marker belongs in world space, or null when the target has no place on the site
   * (a ground-material or whole-site tag). The tool does not know about plants or buildings; the app
   * that owns both resolves this.
   */
  resolveAnchor: (target: TagTarget) => { x: number; y: number; z: number } | null;
  /** Fired when a click resolves to a target. The app opens the bubble. */
  onTarget: (pick: TagPick, screen: { x: number; y: number }) => void;
}

/**
 * What one click resolved to.
 *
 * The ground point travels alongside the target even when the target is a plant or a building,
 * because the bubble offers every scope the click could plausibly have meant — this plant, the ground
 * material under it, the whole site — and the last two are questions about a place. Without the point
 * a click on a shrub could never reach the gravel it stands in.
 */
export interface TagPick {
  target: TagTarget;
  /** Ground position under the pointer, in world metres. */
  point: { x: number; z: number };
}

/** How far above its anchor a pin floats, in metres. Clear of a bench, under a canopy. */
const PIN_LIFT_METERS = 0.9;

export class TagTool {
  readonly overlayObject: THREE.Group;

  private readonly markerRoot: THREE.Group;
  private readonly cursor: THREE.Mesh;
  private readonly raycaster = new THREE.Raycaster();
  /** Owned by the tool for its whole life — the hover cursor. Disposed once, at teardown. */
  private readonly chrome: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[] = [];
  /**
   * Owned by the current marker set, and released on every rebuild.
   *
   * Kept apart from {@link chrome} deliberately: a single list would mean the first rebuild disposed
   * the cursor's geometry along with the markers, and the tool would come back from its second click
   * with an invisible brush.
   */
  private markers: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[] = [];

  private active = false;
  private unsubscribe: (() => void) | null = null;
  private dirty = true;

  private readonly onPointerDown = (event: PointerEvent): void => this.handleClick(event);
  private readonly onPointerMove = (event: PointerEvent): void => this.handleHover(event);

  constructor(private readonly deps: TagToolDeps) {
    this.overlayObject = new THREE.Group();
    this.overlayObject.name = 'tag-overlay';
    this.overlayObject.visible = false;

    this.markerRoot = new THREE.Group();
    this.markerRoot.name = 'tag-markers';
    this.overlayObject.add(this.markerRoot);

    // A ghost ring under the pointer, sized like the tag it would drop. Placing a location tag is
    // choosing an *area*, and choosing one blind then discovering its reach afterwards is the sort of
    // thing that makes a tool feel like a form.
    this.cursor = this.ring(DEFAULT_TAG_RADIUS_METERS * 0.94, DEFAULT_TAG_RADIUS_METERS, 0xffffff, 0.5);
    this.cursor.visible = false;
    this.overlayObject.add(this.cursor);
    // The factories register into the per-rebuild list; the cursor outlives every rebuild, so its
    // resources are moved across to the long-lived one.
    this.chrome.push(...this.markers.splice(0));

    deps.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.unsubscribe = deps.tags.onChange(() => {
      this.dirty = true;
    });
  }

  get isActive(): boolean {
    return this.active;
  }

  // The pointer's appearance belongs to the HUD, which draws a tag glyph for as long as this tool
  // holds the canvas (see `ToolCursor`) — the ring below is the in-world hover mark, not the mouse.
  activate(): void {
    this.active = true;
    this.dirty = true;
    this.overlayObject.visible = true;
    this.deps.domElement.addEventListener('pointermove', this.onPointerMove);
  }

  deactivate(): void {
    this.active = false;
    this.overlayObject.visible = false;
    this.cursor.visible = false;
    this.deps.domElement.removeEventListener('pointermove', this.onPointerMove);
  }

  /**
   * Rebuild markers when the tag set changed, and re-seat them when the ground did.
   *
   * Driven from the render loop rather than from the store's change event so a wand cast that writes
   * a dozen tags rebuilds the overlay once. Rebuilding is cheap — a handful of rings and sprites — and
   * doing it on a flag beats trying to patch individual markers as tags move between targets.
   */
  tick(): void {
    if (!this.active || !this.dirty) return;
    this.dirty = false;
    this.rebuildMarkers();
  }

  /** Force a marker rebuild on the next tick — after a sculpt, or when the site is replaced. */
  invalidate(): void {
    this.dirty = true;
  }

  /* --------------------------------- Markers --------------------------------- */

  private rebuildMarkers(): void {
    this.clearMarkers();

    for (const tag of this.deps.tags.all()) {
      const anchor = this.deps.resolveAnchor(tag.target);
      if (!anchor) continue;
      const color = new THREE.Color(tagCategoryInfo(tag.category).color).getHex();

      if (tag.target.kind === 'location') {
        const radius = tag.target.radiusMeters;
        const ring = this.ring(Math.max(0.08, radius - 0.12), radius, color, 0.75);
        ring.position.set(anchor.x, anchor.y + 0.05, anchor.z);
        this.markerRoot.add(ring);
        this.addLabel(tag, anchor.x, anchor.y + 0.55, anchor.z, color);
        continue;
      }

      // Object tags get a stem and a label rather than a ring: the thing already has a silhouette, so
      // what is missing is a pointer to it, not an outline around it.
      const lift = anchor.y + PIN_LIFT_METERS;
      const stem = this.stem(anchor.x, anchor.y, anchor.z, lift, color);
      this.markerRoot.add(stem);
      this.addLabel(tag, anchor.x, lift + 0.25, anchor.z, color);
    }
  }

  private addLabel(tag: SemanticTag, x: number, y: number, z: number, color: number): void {
    const sprite = this.labelSprite(tag.label, color);
    if (!sprite) return;
    sprite.position.set(x, y, z);
    this.markerRoot.add(sprite);
  }

  /**
   * A text label as a camera-facing sprite.
   *
   * Canvas-drawn rather than DOM-overlaid so the label is occluded and scaled by the scene the same
   * way the thing it names is — a tag on a plant behind the house should go behind the house. Returns
   * null where there is no 2D canvas (a headless test), and the markers simply lose their text rather
   * than the tool failing to construct.
   */
  private labelSprite(text: string, color: number): THREE.Sprite | null {
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null;

    const fontSize = 44;
    const padding = 18;
    context.font = `600 ${fontSize}px system-ui, sans-serif`;
    const width = Math.ceil(context.measureText(text).width) + padding * 2;
    const height = fontSize + padding * 2;
    canvas.width = width;
    canvas.height = height;

    // Measuring resets the context, so the font is set again before it is used to draw.
    context.font = `600 ${fontSize}px system-ui, sans-serif`;
    context.textBaseline = 'middle';
    context.fillStyle = 'rgba(16, 18, 20, 0.82)';
    roundedRect(context, 0, 0, width, height, 16);
    context.fill();
    context.strokeStyle = `#${color.toString(16).padStart(6, '0')}`;
    context.lineWidth = 4;
    roundedRect(context, 2, 2, width - 4, height - 4, 14);
    context.stroke();
    context.fillStyle = '#f4f2ee';
    context.fillText(text, padding, height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    this.markers.push(texture, material);

    const sprite = new THREE.Sprite(material);
    // Scaled to roughly 0.4 m tall at the shipped label size, so a label reads without dominating the
    // plant it sits over.
    const scale = 0.4;
    sprite.scale.set((width / height) * scale, scale, 1);
    sprite.renderOrder = 12;
    return sprite;
  }

  private ring(inner: number, outer: number, color: number, opacity: number): THREE.Mesh {
    const geometry = new THREE.RingGeometry(inner, outer, 64);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    this.markers.push(geometry, material);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 11;
    return mesh;
  }

  private stem(x: number, baseY: number, z: number, topY: number, color: number): THREE.Line {
    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x, baseY + 0.05, z),
      new THREE.Vector3(x, topY, z),
    ]);
    const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.85, depthTest: false });
    this.markers.push(geometry, material);
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 11;
    return line;
  }

  private clearMarkers(): void {
    for (const child of [...this.markerRoot.children]) this.markerRoot.remove(child);
    // A rebuild reuses nothing, so everything the previous set owned is released here — otherwise a
    // long session accumulates a texture per label per rebuild.
    for (const item of this.markers.splice(0)) item.dispose();
  }

  /* --------------------------------- Picking --------------------------------- */

  private handleHover(event: PointerEvent): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;
    const ground = this.pickGround(terrain, event);
    if (!ground) {
      this.cursor.visible = false;
      return;
    }
    this.cursor.visible = true;
    this.cursor.position.set(ground.x, terrain.sampleHeight(ground.x, ground.z) + 0.04, ground.z);
  }

  /**
   * Resolve a click to a target.
   *
   * Same precedence as the wand — plant, then structure, then ground — because it answers the same
   * question ("what is the pointer over?") and two tools that answer it differently would be a bug
   * the user experiences as the app being unpredictable.
   */
  private handleClick(event: PointerEvent): void {
    if (!this.active || event.button !== 0) return;
    const terrain = this.deps.terrain();
    if (!terrain) return;

    event.preventDefault();
    event.stopPropagation();

    const plantId = this.pickId(this.deps.floraRoot, 'floraInstanceId', event);
    const objectId = plantId ? null : this.pickId(this.deps.siteRoot, 'siteObjectId', event);
    const ground = this.pickGround(terrain, event);

    // The ground point is what the bubble's other scopes hang off, so a click that hit only a canopy
    // still needs one; fall back to the picked entity's own footing rather than dropping the click.
    const point =
      ground ??
      pointOf(this.deps.resolveAnchor(anchorTarget(plantId, objectId))) ??
      null;
    if (!point) return;

    const target: TagTarget = plantId
      ? { kind: 'plant', instanceId: plantId }
      : objectId
        ? { kind: 'structure', objectId }
        : { kind: 'location', x: round2(point.x), z: round2(point.z), radiusMeters: DEFAULT_TAG_RADIUS_METERS };

    this.deps.onTarget(
      { target, point: { x: round2(point.x), z: round2(point.z) } },
      { x: event.clientX, y: event.clientY },
    );
  }

  private aim(event: PointerEvent): void {
    const rect = this.deps.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.deps.getCamera());
  }

  private pickGround(terrain: Terrain, event: PointerEvent): { x: number; z: number } | null {
    this.aim(event);
    const point = this.raycaster.intersectObject(terrain.mesh, false)[0]?.point;
    if (!point) return null;
    return {
      x: clamp(point.x, -terrain.widthMeters / 2, terrain.widthMeters / 2),
      z: clamp(point.z, -terrain.lengthMeters / 2, terrain.lengthMeters / 2),
    };
  }

  private pickId(root: THREE.Object3D, key: string, event: PointerEvent): string | null {
    this.aim(event);
    let current: THREE.Object3D | null = this.raycaster.intersectObjects(root.children, true)[0]?.object ?? null;
    while (current) {
      const id = current.userData[key] as unknown;
      if (typeof id === 'string') return id;
      current = current.parent;
    }
    return null;
  }

  dispose(): void {
    this.deps.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.clearMarkers();
    for (const item of this.chrome.splice(0)) item.dispose();
    this.overlayObject.removeFromParent();
  }
}

/** The target whose anchor stands in for a missed ground ray. */
function anchorTarget(plantId: string | null, objectId: string | null): TagTarget {
  if (plantId) return { kind: 'plant', instanceId: plantId };
  if (objectId) return { kind: 'structure', objectId };
  return { kind: 'site' };
}

function pointOf(anchor: { x: number; y: number; z: number } | null): { x: number; z: number } | null {
  return anchor ? { x: anchor.x, z: anchor.z } : null;
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
